import {
  FC,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useIsMobile, useSwipeGesture } from "@repo/edtech-shared";
import { Button } from "@repo/edtech-ui-kit";
import {
  Icon,
  IconButton,
  IconStrokeChevronRightSmall,
  IconStrokeCrossSmall,
  InputCode,
  ScopedModal,
  Typography,
} from "@repo/wb-ui-kit";

import { OriginAsset, t } from "../../../origins";

import st from "./ConfirmCodeModal.module.scss";

type Props = {
  isOpen: boolean,
  onClose: () => void,
  onConfirm: (code: string) => void,
  onResendCode?: () => Promise<number | void>,
  phoneNumber?: string,
  codeLength?: number,
  resendTimeout?: number,
  title?: string,
  subtitle?: ReactNode,
  errorMessage?: string,
  isLoading?: boolean,
  isSuccess?: boolean,
  successTitle?: string,
  successSubtitle?: ReactNode,
};

const MOBILE_WIDTH = 768;

export const ConfirmCodeModal: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  onResendCode,
  phoneNumber = "+7 000 000-00-00",
  codeLength = 6,
  resendTimeout = 30,
  title,
  subtitle,
  errorMessage = "",
  isLoading = false,
  isSuccess = false,
  successTitle,
  successSubtitle,
}) => {
  const [ code, setCode ] = useState("");
  const [ timer, setTimer ] = useState(resendTimeout);
  const [ swipeOffset, setSwipeOffset ] = useState(0);

  const isMobile = useIsMobile(MOBILE_WIDTH);
  const isError = Boolean(errorMessage);
  const canResend = timer === 0;

  const { swipeHandlers } = useSwipeGesture({
    onSwipeDown: () => {
      onClose();
      setSwipeOffset(0);
    },
    onSwipeMove: (deltaY) => {
      if (deltaY > 0) setSwipeOffset(deltaY);
    },
    onSwipeEnd: () => setSwipeOffset(0),
    threshold: 80,
    maxDistance: 200,
    enabled: isOpen && isMobile,
  });

  useEffect(() => {
    if (!isOpen) {
      setCode("");
      setSwipeOffset(0);
      return;
    }
    setTimer(resendTimeout);
  }, [ isOpen, resendTimeout ]);

  useEffect(() => {
    if (!isOpen || canResend) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [ isOpen, canResend ]);

  const handleSubmit = useCallback((value?: string) => {
    const submitCode = value || code;
    if (submitCode.length !== codeLength || isLoading) return;
    onConfirm(submitCode);
  }, [
    code,
    codeLength,
    isLoading,
    onConfirm,
  ]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
  }, [ handleSubmit ]);

  const handleResendCode = async () => {
    if (!canResend) return;
    const newTtl = await onResendCode?.();
    if (typeof newTtl === "number") {
      setTimer(newTtl);
    }
  };

  const paperStyle = swipeOffset > 0 ?
    { transform: `translateY(${swipeOffset}px)`, transition: "none" } :
    undefined;

  return (
    <ScopedModal
      isOpen={isOpen}
      lockScroll={true}
      paperClassName={
        isMobile ?
          st.modal_paper_mobile :
          st.modal_paper
      }
      paperPlacement={
        isMobile ?
          "bottom" :
          "center"
      }
      paperStyle={paperStyle}
      showCloseButton={!isMobile}
      onClose={onClose}
    >
      <div
        className={st.modal_content}
        {...swipeHandlers}
      >
        {
          isMobile && (
            <div className={st.swipe_area}>
              <div className={st.swipe_handle} />
            </div>
          )
        }

        <div className={st.content}>
          {
            isSuccess ?
              (
                <>
                  <div className={st.success_icon}>
                    <OriginAsset
                      assetKey="confirmCodeModalSuccessIcon"
                    />
                  </div>
                  <Typography
                    as="h2"
                    className={st.title}
                    variant="title1"
                  >
                    {successTitle || t("confirmCodeModal.successTitle")}
                  </Typography>
                  <Typography
                    as="p"
                    className={st.subtitle}
                    colors="secondary"
                    variant="body"
                  >
                    {successSubtitle || t("confirmCodeModal.successSubtitle")}
                  </Typography>
                  <Button
                    className={st.confirm_button}
                    onClick={onClose}
                  >
                    {t("confirmCodeModal.closeButton")}
                  </Button>
                </>
              ) :
              (
                <>
                  <Typography
                    className={st.title}
                    variant={
                      isMobile ?
                        "title1" :
                        "title2"
                    }
                  >
                    {title || t("confirmCodeModal.title")}
                  </Typography>
                  <Typography
                    as="p"
                    className={st.subtitle}
                    colors="secondary"
                    variant="body"
                  >
                    {
                      subtitle || (
                        <>
                          {t("confirmCodeModal.subtitle")}
                          {" "}
                          {phoneNumber}
                        </>
                      )
                    }
                  </Typography>

                  <div
                    className={st.code_wrapper}
                    onKeyDown={handleKeyDown}
                  >
                    <InputCode
                      autoFocus
                      charsCount={codeLength}
                      isLoading={isLoading}
                      value={code}
                      onChange={setCode}
                      onPasteComplete={handleSubmit}
                    />
                    {
                      isError && (
                        <Typography
                          as="p"
                          className={st.error_message}
                          variant="body"
                        >
                          {errorMessage}
                        </Typography>
                      )
                    }
                  </div>

                  <Button
                    className={st.confirm_button}
                    disabled={code.length !== codeLength || isLoading}
                    onClick={() => handleSubmit()}
                  >
                    {t("confirmCodeModal.confirmButton")}
                  </Button>

                  <Typography
                    as="p"
                    colors="secondary"
                    variant="body"
                  >
                    {
                      canResend ?
                        (
                          <button
                            className={st.resend_button}
                            type="button"
                            onClick={handleResendCode}
                          >
                            {t("confirmCodeModal.resendButton")}
                            <Icon
                              className={st.resend_icon}
                              size={16}
                              source={IconStrokeChevronRightSmall}
                            />
                          </button>
                        ) :
                        (
                          <>{t("confirmCodeModal.resendTimer", { timer })}</>
                        )
                    }
                  </Typography>
                </>
              )
          }
        </div>

        {
          isMobile && !isSuccess && (
            <IconButton
              className={st.close_button}
              colors="secondary"
              onClick={onClose}
            >
              <Icon source={IconStrokeCrossSmall} />
            </IconButton>
          )
        }
      </div>
    </ScopedModal>
  );
};
